import type { DeckardIcon } from '@assets/icons';
import { Group } from '@components/layout';
import {
  FormControlLabel,
  Radio as MuiRadio,
  useTheme,
  type FormControlLabelProps,
  type RadioProps as MuiRadioProps,
} from '@mui/material';

export type RadioProps = MuiRadioProps & { label?: FormControlLabelProps['label'] };

export const Radio = ({ disabled, label, ...props }: RadioProps) => {
  const { palette } = useTheme();
  return (
    <FormControlLabel
      control={
        <MuiRadio
          disableRipple
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="radio-icon-unchecked"
            >
              <circle cx="8" cy="8" r="6" fill={palette.radio.fill.unchecked} strokeWidth="4" />
            </svg>
          }
          checkedIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="radio-icon-checked"
            >
              <circle cx="8" cy="8" r="6" fill={palette.radio.fill.checked} strokeWidth="4" />
            </svg>
          }
          disabled={disabled}
          {...props}
        />
      }
      sx={{ marginRight: label ? 4 : 0 }}
      label={label}
    />
  );
};

type RadioIconProps = Omit<MuiRadioProps, 'icon'> & { icon: DeckardIcon };

export const RadioIcon = ({ disabled, icon: Icon, sx, checked, ...props }: RadioIconProps) => {
  const { palette } = useTheme();
  return (
    <Group sx={{ height: '34px', width: '34px', alignItems: 'center', justifyContent: 'center' }}>
      <MuiRadio
        disableRipple
        icon={<Icon color={palette.radio.icon.default} />}
        checked={checked}
        checkedIcon={<Icon color={palette.radio.icon.checked} />}
        disabled={disabled}
        sx={{
          height: '28px',
          width: '28px',
          borderRadius: 1,
          '&:hover': {
            border: `1px solid ${palette.radio.icon.hover}`,
            '& .deckard-icon': {
              color: palette.radio.icon.hover,
            },
          },
          '&:active': {
            borderColor: palette.input.border.active,
            '& .deckard-icon': {
              color: palette.input.border.active,
            },
          },
          '&.Mui-checked': {
            backgroundColor: palette.radio.icon.checkedBackground,
          },
          ...sx,
        }}
        {...props}
      />
    </Group>
  );
};
